// Worker
import { Worker } from "../Worker";
// Action Creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { CacheHydrationRequestType, CacheHydrationState, GlobalViewSettingKeys, LegacyViewStoreTaskChatPaneOpenKey, PlannerPersistedObjectStoreIndexNames, PlannerV2AsyncStorageSchemaVersionV1, ViewStoreTaskEditorActiveSidePanelKey, ViewStoreTaskEditorSidePanelWidthKey, ViewStoreViewConfigKey } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { RolloutStage } from "@ms/uno-constants/lib/local/RolloutStage";
import { SubRouteKeys, SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { PersistedObjectStoreSchema } from "@ms/planner-models/lib/local/client/persistence/PersistedAsyncStorageSchema";
import { PlannerDefaultPlanIdKey } from "@ms/planner-models/lib/local/client/persistence/defaultPlan/IPlannerDefaultPlanIdRecord";
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { TaskEditorSidePanelType } from "@ms/planner-models/lib/local/types/TaskEditorSidePanelType";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Utilities
import { validateDynamicFilter } from "@ms/planner-models/lib/local/utilities/DynamicFilterUtilities";
import { convertUnoFiltersToEvoFilters, convertUnoGridColumnWidthsToEvoGridColumnWidths, convertUnoGroupingStatesToEvoGroupingStates, convertUnoSortCriteriaToEvoSortCriteria } from "@ms/planner-models/lib/local/utilities/UnoEvoConversionUtilities";
import { GridViewName } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { isPlannerEntityId } from "@ms/uno-hostadaptors/lib/local/metaOS/teams/legacyBootRoutes";
import { extractPrimaryViewIdFromRoute, getValueForKey } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { IndexedDBStorage } from "@ms/uno-utilities/lib/local";
import { getCurrentDateWithoutTimestamp } from "@ms/uno-utilities/lib/local/DateUtilities";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { collection, status } from "@ms/uno-utilities/lib/local/storage/TableNameUtilities";
import { Temporal } from "temporal-polyfill-lite";
import { getTtlInDays, isPersistedStatusStale } from "./CacheTtlUtilities";
import { deserialize } from "./serialization/SerializationHandler";
export class CacheDataFetchWorker {
    init() {
        if (this.initialized) {
            return;
        }
        const shouldHydratePlannerDefaultPlanId = this.shouldHydratePlannerDefaultPlanId();
        if (!shouldHydratePlannerDefaultPlanId) {
            this.fetchPersistedMruIfNeeded();
        }
        // Load pinned tasks (local-only task favorites) on init — gated on task persistence
        if (this.configProvider().settings.evoPersistenceSettings.tasks && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            if (this.configProvider().flights.EnableNewMyTasksExperience) {
                this.fetchPersistedPinnedTasksData();
                this.fetchPersistedTaskOrderData();
            }
        }
        // Load saved dynamic filters from local storage on init — gated on the dynamic filter persistence flight
        if (this.configProvider().flights.EnableDynamicFilterPersistence) {
            this.fetchPersistedDynamicFilterData();
        }
        // Load device-local user photos (base64 data URIs) on init.
        if (this.configProvider().settings.evoPersistenceSettings.userPhotos && !this.configProvider().changeGates.DisableUserPhotoDataUri // Skip when the data-URI changegate is on, since object URLs are not serializable.
        ) {
            this.fetchPersistedUserPhotosData();
        }
        // Load device-local modern group photos (base64 data URIs) on init.
        if (this.configProvider().settings.evoPersistenceSettings.modernGroupPhotos && !this.configProvider().changeGates.DisableUserPhotoDataUri // Skip when the data-URI changegate is on, since object URLs are not serializable.
        ) {
            this.fetchPersistedModernGroupPhotosData();
        }
        // Hydrate user profiles on init so facepile assignee names are available before any network call.
        if (this.configProvider().settings.evoPersistenceSettings.users) {
            this.fetchPersistedUserData();
        }
        // For loading data other than Mru
        this.routeStore.subscribeToStore(this.handleRouteStoreChange);
        if (shouldHydratePlannerDefaultPlanId) {
            void this.fetchPersistedPlannerDefaultPlanId().then(()=>{
                this.fetchPersistedMruIfNeeded();
                this.fetchPersistedDataAsPerRouteType();
            });
        } else {
            this.fetchPersistedDataAsPerRouteType();
        }
        // Hydrate global preferences once at init (not on route changes, since they are not route-specific).
        if (this.configProvider().settings.evoPersistenceSettings.view) {
            if (this.configProvider().flights.EnableTaskEditorPanel) {
                this.fetchAndHydrateTaskEditorRenderMode();
            }
            this.fetchAndHydrateTaskEditorActiveSidePanel();
            this.fetchAndHydrateTaskEditorSidePanelWidth();
        }
        this.initialized = true;
    }
    fetchPersistedMruIfNeeded() {
        if (this.configProvider().settings.evoPersistenceSettings.mru && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            this.fetchPersistedLeftNavData();
        }
    }
    shouldHydratePlannerDefaultPlanId() {
        return this.configProvider().settings.evoPersistenceSettings.tasks && this.configProvider().flights.EnableMyTasksDefaultPlan;
    }
    get name() {
        return Worker.CacheDataFetch;
    }
    /**
     * Stops the worker
     */ stop() {
        this.routeStore.unsubscribeFromStore(this.handleRouteStoreChange);
        this.initialized = false;
    }
    /**
     * Emits the StoreHydration performance marker for a cache hydration checkpoint
     * @param state hydration checkpoint being reported
     * @param viewName view whose data is being hydrated
     */ markHydration(state, viewName) {
        this.loggers.performanceMarker.mark(MarkerTag.StoreHydration, {
            state,
            viewName
        });
    }
    /**
     * Method to read view config from the new localStorage key
     * @param viewId The id of the view
     * @returns The view config resource if found, null otherwise
     */ readViewConfigFromLocalStorage(viewId) {
        if (!this.persistedStorage) {
            return null;
        }
        try {
            const data = this.persistedStorage.getItem(ViewStoreViewConfigKey);
            if (!data) {
                return null;
            }
            const entries = JSON.parse(data);
            const viewIdString = toViewIdString(viewId);
            const entry = entries.find((e)=>e.k === viewIdString);
            if (entry) {
                return {
                    viewId: viewIdString,
                    ...entry.v,
                    taskFilter: JSON.stringify(entry.v.taskFilter)
                };
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e05d65d /* tag_4b3z3 */ , TraceLevel.Warning, `readViewConfigFromLocalStorage parse failure [Error=${error}]`);
        }
        return null;
    }
    /**
     * Method to read view config from Uno persisted storage for backward compatibility
     * We will eventually be able to remove this method once we are sure that all users have the new format of view config persisted
     * @param viewId The id of the view
     */ readViewConfigFromUno(viewId) {
        let config = null;
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        const serializedEmptyArray = JSON.stringify([]);
        if (!this.persistedStorage) {
            this.loggers.traceLogger.logTrace(0x1e0da293 /* tag_4d0kt */ , TraceLevel.Info, `UnoViewConfigReadSkipped`);
            return null;
        }
        try {
            // Filters
            const unoFilters = JSON.parse(this.persistedStorage.getItem(`vstore-tf`) ?? serializedEmptyArray);
            const migratedFilters = convertUnoFiltersToEvoFilters(unoFilters);
            // Group by
            const unoGroupingStates = JSON.parse(this.persistedStorage.getItem(`vstore-tgs`) ?? serializedEmptyArray);
            const migratedGroupingOptions = convertUnoGroupingStatesToEvoGroupingStates(unoGroupingStates);
            // Sort Criteria
            const unoSortCriterias = JSON.parse(this.persistedStorage.getItem(`vstore-sc`) ?? serializedEmptyArray);
            const migratedSortCriteria = convertUnoSortCriteriaToEvoSortCriteria(unoSortCriterias);
            // Grid column widths
            const gridColumnWidths = JSON.parse(this.persistedStorage.getItem(`vstore-gcw`) ?? serializedEmptyArray);
            const migratedGridColumnWidths = convertUnoGridColumnWidthsToEvoGridColumnWidths(gridColumnWidths);
            switch(viewId.name){
                case ViewName.MyDayPage:
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `2_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                case ViewName.MyTasksPage:
                    const myTasksPageTaskFilter = migratedFilters.find((f)=>f.k === `1_${userId}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: myTasksPageTaskFilter ? JSON.stringify(myTasksPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `1_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                case ViewName.PlanPage:
                    const planPageTaskFilter = migratedFilters.find((f)=>f.k === `0_${viewId.partitionKey}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: planPageTaskFilter ? JSON.stringify(planPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `0_${viewId.partitionKey}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        ganttZoomScaleByViewType: null,
                        isUnscheduledColumnVisible: null
                    };
                    break;
                default:
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e0da25b /* tag_4d0j1 */ , TraceLevel.Warning, `UnoViewConfigParseFailure [Error=${error}]`);
            return null;
        }
        if (config?.taskFilter !== null || config?.taskGrouping !== null || config?.sortCriteria !== null || config?.gridColumnWidths !== null) {
            this.loggers.traceLogger.logTrace(0x1e0db594 /* tag_4d1wu */ , TraceLevel.Info, `UnoViewConfigReadSuccess`);
            return config;
        }
        return null;
    }
    /**
     * Constructor for the CacheDataFetchWorker
     * @param acLoader The action creator loader
     * @param storeLoader The store loader
     * @param persistedStorage persisted storage instance
     * @param persistentAsyncStorage persisted async storage instance
     * @param configProvider The config provider
     * @param loggers The loggers context
     */ constructor(acLoader, storeLoader, persistedStorage, persistentAsyncStorage, configProvider, loggers){
        this.acLoader = acLoader;
        this.storeLoader = storeLoader;
        this.persistedStorage = persistedStorage;
        this.persistentAsyncStorage = persistentAsyncStorage;
        this.configProvider = configProvider;
        this.loggers = loggers;
        /** Indicates if the worker has been initialized */ this.initialized = false;
        /** The route store */ this.routeStore = this.storeLoader.load(Store.Route);
        this.fetchPersistedPlannerDefaultPlanId = async ()=>{
            try {
                const record = await this.persistentAsyncStorage.read(TableName.PlannerDefaultPlanId, PlannerDefaultPlanIdKey);
                if (record) {
                    const deserializedRecord = deserialize(TableName.PlannerDefaultPlanId, record);
                    if (deserializedRecord.key !== PlannerDefaultPlanIdKey || !isPlannerEntityId(deserializedRecord.planId, this.configProvider)) {
                        this.loggers.traceLogger.logTrace(0x1d7d854f /* tag_35yvp */ , TraceLevel.Warning, `Ignoring invalid Planner Default Plan ID cache record`);
                        return;
                    }
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydratePlannerDefaultPlanId(deserializedRecord);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d7d854e /* tag_35yvo */ , TraceLevel.Error, `fetchPersistedPlannerDefaultPlanId [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to Load Persisted Data from the browser cache as per the current route
     */ this.fetchPersistedDataAsPerRouteType = ()=>{
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            const subRouteType = this.routeStore.getCurrentSubRouteType();
            const currentSubRoute = this.routeStore.getCurrentSubRoute();
            const viewId = extractPrimaryViewIdFromRoute(subRouteType, currentSubRoute, userId);
            // Fetch and hydrate persisted tasks and related data as per the current route type if persistence is enabled for tasks
            if (this.configProvider().settings.evoPersistenceSettings.tasks) {
                switch(subRouteType){
                    case SubRouteType.MyDay:
                        this.fetchPersistedMyDayData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyTasks:
                        this.fetchPersistedMyTasksData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyPlans:
                        break;
                    case SubRouteType.BasicPlan:
                        this.fetchPersistedPlanData(viewId.partitionKey);
                        break;
                    default:
                        break;
                }
            }
            // Fetch and hydrate view config if persistence is enabled for view config
            if (this.configProvider().settings.evoPersistenceSettings.view) {
                this.fetchAndHydrateViewConfig(viewId);
            }
            // Route-driven cache hydration kill switch — see DisableTaskHydrationFromRouteCache in IChangeGates.
            if (this.configProvider().settings.evoPersistenceSettings.tasks && !this.configProvider().changeGates.DisableTaskHydrationFromRouteCache) {
                const taskId = getValueForKey(currentSubRoute, SubRouteKeys.Task);
                if (taskId) {
                    this.fetchPersistedTaskAndChat(taskId);
                }
            }
        };
        /**
     * Method to handle route store change events to load the relevant persisted data from cache as
     */ this.handleRouteStoreChange = ()=>{
            this.fetchPersistedDataAsPerRouteType();
        };
        /**
     * Method to hydrate all mru data from the persisted async storage
     */ this.fetchPersistedLeftNavData = async ()=>{
            this.markHydration(CacheHydrationState.Start, ViewName.LeftNavContainer);
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.Mru);
                if (items && Object.keys(items).length > 0) {
                    try {
                        const mruItems = [];
                        for (const key of Object.keys(items)){
                            const item = deserialize(TableName.Mru, items[key]);
                            mruItems.push(item);
                        }
                        this.acLoader.load(ActionCreator.CacheDataFetch).hydrateMruData(mruItems, ()=>this.markHydration(CacheHydrationState.End, ViewName.LeftNavContainer));
                    } catch (error) {
                        this.loggers.traceLogger.logTrace(0x1e103049 /* tag_4edbj */ , TraceLevel.Error, `fetchPersistedLeftNavData Deserialization failure [Error=${error}]`);
                    }
                } else {
                    this.loggers.traceLogger.logTrace(0x1e103048 /* tag_4edbi */ , TraceLevel.Info, `No entries in cache [Store=MruStore]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103047 /* tag_4edbh */ , TraceLevel.Error, `fetchPersistedLeftNavData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate device-local user photos (base64 data URIs) from IndexedDB into the UserStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedUserPhotosData = async ()=>{
            const photos = await this.fetchPersistedPhotosData(TableName.UserPhotos);
            this.acLoader.load(ActionCreator.CacheDataFetch).hydrateUserPhotos(photos);
        };
        /**
     * Method to hydrate device-local modern group photos (base64 data URIs) from IndexedDB into the ModernGroupsStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedModernGroupPhotosData = async ()=>{
            const photos = await this.fetchPersistedPhotosData(TableName.ModernGroupPhotos);
            this.acLoader.load(ActionCreator.CacheDataFetch).hydrateModernGroupPhotos(photos);
        };
        /**
     * Shared helper to read device-local photos (base64 data URIs) and their persisted status from IndexedDB.
     * Photos that are stale per the eviction TTL policy are skipped so they are never restored into the store.
     * This mirrors the eviction worker's staleness gate: if a stale photo were restored, the cache-update worker
     * would re-persist it right after the eviction worker purged it (the restore -> evict -> rebuild-from-store
     * race), defeating TTL-based eviction. Non-stale photos are restored as-is, including those persisted in a
     * non-Present state (e.g. 403/404), so the module layer can inspect the restored status and suppress refetch.
     * The action creator guards against an empty result, so the photos are returned as-is.
     * @param dataTable photo data table to read from
     * @returns the restored (non-stale) photos (empty when none were read)
     */ this.fetchPersistedPhotosData = async (dataTable)=>{
            const photos = [];
            try {
                const [items, statusItems] = await Promise.all([
                    this.persistentAsyncStorage.readAll(dataTable),
                    this.persistentAsyncStorage.readAll(status(dataTable))
                ]);
                // Restore every persisted entry, keyed off the union of the data and status tables. A failed fetch
                // (e.g. 403/404) only writes a status row and no photo data, so iterating the data table alone would
                // drop those entries and the failure status would be lost across sessions, causing the photo to be
                // refetched every session. Including status-only keys preserves the prior failure so the module layer
                // can suppress the refetch.
                const allKeys = Object.keys({
                    ...items,
                    ...statusItems
                });
                // Compute the TTL and a single "now" once for the whole batch so staleness is evaluated identically
                // to the eviction worker (which also snapshots a single instant per run).
                const ttlInDays = getTtlInDays(this.configProvider, dataTable);
                const now = Temporal.Now.zonedDateTimeISO("UTC");
                for (const key of allKeys){
                    const persistedStatus = statusItems?.[key];
                    // Skip stale photos using the same TTL gate as the eviction worker, so the cache-update worker
                    // cannot rebuild-from-store what the eviction worker purges.
                    if (persistedStatus === undefined || isPersistedStatusStale(persistedStatus, ttlInDays, now)) {
                        continue;
                    }
                    // A status-only entry (no persisted photo data) represents a prior failed fetch; restore it with a
                    // null photo so only its status is rehydrated.
                    const persistedItem = items?.[key];
                    const photo = persistedItem !== undefined ? deserialize(dataTable, persistedItem) : null;
                    // Restore the persisted status (including its original fetch timestamp) so fetch-time staleness
                    // evaluation reflects the true last-fetch time rather than the time of rehydration.
                    const photoStatus = deserialize(status(dataTable), persistedStatus);
                    photos.push({
                        id: key,
                        photo,
                        status: photoStatus
                    });
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d8236d2 /* tag_3691s */ , TraceLevel.Error, `fetchPersistedPhotosData [Table=${dataTable}] [Error=${ErrorUtilities.getMessage(error)}]`);
            }
            return photos;
        };
        this.fetchPersistedUserData = async ()=>{
            const users = await this.fetchPersistedUsersData();
            if (users.length > 0) {
                this.acLoader.load(ActionCreator.CacheDataFetch).hydrateUsers(users);
            }
        };
        this.fetchPersistedUsersData = async ()=>{
            const results = [];
            try {
                const [items, statusItems] = await Promise.all([
                    this.persistentAsyncStorage.readAll(TableName.Users),
                    this.persistentAsyncStorage.readAll(status(TableName.Users))
                ]);
                const allKeys = Object.keys({
                    ...items,
                    ...statusItems
                });
                const ttlInDays = getTtlInDays(this.configProvider, TableName.Users);
                const now = Temporal.Now.zonedDateTimeISO("UTC");
                for (const key of allKeys){
                    const persistedStatus = statusItems?.[key];
                    if (persistedStatus === undefined || persistedStatus.state !== StoreEntityState.Present || isPersistedStatusStale(persistedStatus, ttlInDays, now)) {
                        continue;
                    }
                    const persistedItem = items?.[key];
                    if (persistedItem === undefined) {
                        continue;
                    }
                    const user = deserialize(TableName.Users, persistedItem);
                    const userStatus = deserialize(status(TableName.Users), persistedStatus);
                    results.push({
                        user,
                        status: userStatus
                    });
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d78f1d0 /* tag_34phq */ , TraceLevel.Error, `fetchPersistedUsersData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
            return results;
        };
        /**
     * Method to hydrate local pinned-task state from IndexedDB into the PinnedTasksStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedPinnedTasksData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.PinnedTasks);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const pinnedTasks = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.PinnedTasks, items[key]);
                    pinnedTasks.push(item);
                }
                this.acLoader.load(ActionCreator.PinnedTasks).hydrateEntityData(pinnedTasks);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d89a2cd /* tag_380ln */ , TraceLevel.Error, `fetchPersistedPinnedTasksData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to hydrate local task-order overrides from IndexedDB into TaskOrderStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedTaskOrderData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.TaskOrder);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const taskOrders = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.TaskOrder, items[key]);
                    taskOrders.push(item);
                }
                this.acLoader.load(ActionCreator.TaskOrder).hydrateEntityData(taskOrders);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d88258e /* tag_38cwo */ , TraceLevel.Error, `fetchPersistedTaskOrderData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to hydrate locally saved dynamic filters from IndexedDB into DynamicFilterStore.
     * Each record is validated before hydration; invalid records are dropped and logged so the
     * store never holds corrupt data.
     */ this.fetchPersistedDynamicFilterData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.DynamicFilter);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const dynamicFilters = [];
                for (const key of Object.keys(items)){
                    try {
                        const filter = deserialize(TableName.DynamicFilter, items[key]);
                        const validation = validateDynamicFilter(filter);
                        if (!validation.isValid) {
                            this.loggers.traceLogger.logTrace(0x1d81c1d0 /* tag_362hq */ , TraceLevel.Warning, `fetchPersistedDynamicFilterData dropped invalid filter [filterId=${filter.id}] [error=${validation.errorMessage}]`);
                            continue;
                        }
                        dynamicFilters.push(filter);
                    } catch (recordError) {
                        this.loggers.traceLogger.logTrace(0x1d81c1cf /* tag_362hp */ , TraceLevel.Warning, `fetchPersistedDynamicFilterData dropped unreadable filter record [key=${key}] [Error=${ErrorUtilities.getMessage(recordError)}]`);
                    }
                }
                this.acLoader.load(ActionCreator.DynamicFilter).hydrateEntityData(dynamicFilters);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d81c1ce /* tag_362ho */ , TraceLevel.Error, `fetchPersistedDynamicFilterData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to load my day page data from persistent async storage and hydrate the corresponding stores
     */ this.fetchPersistedMyDayData = async (userId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            // Only hydrate from cache when My Day tasks are absent from the store
            if (taskStore.getMyDayTasks().status.state !== StoreEntityState.Absent) {
                return;
            }
            this.markHydration(CacheHydrationState.Start, ViewName.MyDayPage);
            try {
                const [tasksInMyDayStatus, tasks] = await Promise.all([
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInMyDayOfUser), userId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksMyDayDateIndex, getCurrentDateWithoutTimestamp())
                ]);
                if (tasksInMyDayStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103046 /* tag_4edbg */ , TraceLevel.Info, `MyDayPage collection status not present in cache [status=${tasksInMyDayStatus?.state}]`);
                    return;
                }
                if (Object.keys(tasks).length === 0) {
                    this.loggers.traceLogger.logTrace(0x1e103044 /* tag_4edbe */ , TraceLevel.Info, `No myDay tasks in cache [Store=TaskStore]`);
                }
                try {
                    const taskItems = [];
                    for (const key of Object.keys(tasks)){
                        const item = deserialize(TableName.Tasks, tasks[key]);
                        taskItems.push(item);
                    }
                    const deltaLink = tasksInMyDayStatus?.deltaLink ?? undefined;
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntities([
                        {
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.Task,
                            relationship: QueryRelationship.TasksInMyDayOfUser,
                            parentId: this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id,
                            data: taskItems,
                            deltaLink
                        }
                    ], ()=>this.markHydration(CacheHydrationState.End, ViewName.MyDayPage));
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e103045 /* tag_4edbf */ , TraceLevel.Error, `fetchPersistedMyDayData Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103043 /* tag_4edbd */ , TraceLevel.Error, `fetchPersistedMyDayData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate My Tasks page data from the persisted async storage
     */ this.fetchPersistedMyTasksData = async (pivot)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            const shouldHydrateAssignedTasks = (pivot === MyTasksViewPivotType.AssignedToMe || pivot === MyTasksViewPivotType.All) && taskStore.getTasksAssignedToUser(userId).status.state === StoreEntityState.Absent;
            const shouldHydrateDefaultPlan = this.configProvider().flights.EnableMyTasksDefaultPlan && (pivot === MyTasksViewPivotType.PrivateTasks || pivot === MyTasksViewPivotType.All);
            if (!shouldHydrateAssignedTasks && !shouldHydrateDefaultPlan) {
                return;
            }
            const hydrations = [];
            this.markHydration(CacheHydrationState.Start, ViewName.MyTasksPage);
            try {
                // Hydrate ATM tasks only when absent, but continue independently to Planner private-task hydration below.
                if (shouldHydrateAssignedTasks) {
                    const [tasks, taskStatus] = await Promise.all([
                        this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksAssigneeIdentityIdIndex, userId),
                        this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksAssignedToUser), userId)
                    ]);
                    if (taskStatus?.state !== StoreEntityState.Present) {
                        this.loggers.traceLogger.logTrace(0x1e103042 /* tag_4edbc */ , TraceLevel.Info, `MyTasksPage ATM tasks collection status not present in cache [status=${taskStatus?.state}]`);
                    } else {
                        if (Object.keys(tasks).length === 0) {
                            this.loggers.traceLogger.logTrace(0x1e103040 /* tag_4edba */ , TraceLevel.Info, `No ATM tasks in cache [Store=TaskStore]`);
                        }
                        try {
                            const taskItems = [];
                            for (const key of Object.keys(tasks)){
                                const item = deserialize(TableName.Tasks, tasks[key]);
                                taskItems.push(item);
                            }
                            const deltaLink = taskStatus?.deltaLink ?? undefined;
                            hydrations.push({
                                type: CacheHydrationRequestType.Collection,
                                entityType: EntityType.Task,
                                relationship: QueryRelationship.TasksAssignedToUser,
                                parentId: userId,
                                data: taskItems,
                                deltaLink
                            });
                        } catch (error) {
                            this.loggers.traceLogger.logTrace(0x1e103041 /* tag_4edbb */ , TraceLevel.Error, `fetchPersistedMyTasksData Deserialization failure [Error=${error}]`);
                        }
                    }
                }
                if (shouldHydrateDefaultPlan) {
                    const planId = this.storeLoader.load(Store.Plan).getDefaultPlanId(DefaultPlanSource.Planner);
                    if (planId) {
                        hydrations.push(...await this.fetchPersistedPlannerDefaultPlanData(planId));
                    }
                }
                if (hydrations.length > 0) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntities(hydrations, ()=>this.markHydration(CacheHydrationState.End, ViewName.MyTasksPage));
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103023 /* tag_4eda9 */ , TraceLevel.Error, `fetchPersistedMyTasksData [Error=${error}]`);
            }
        };
        /**
     * Collects the cached Planner default plan and its tasks for hydration
     * @param planId id of the default plan
     * @returns hydration requests for the plan and its tasks, or an empty list when the cache holds no usable data
     */ this.fetchPersistedPlannerDefaultPlanData = async (planId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            if (taskStore.getTasksForPlan(planId).status.state === StoreEntityState.Present) {
                return [];
            }
            const [plan, planStatus, tasks, tasksInPlanStatus] = await Promise.all([
                this.persistentAsyncStorage.read(TableName.Plan, planId),
                this.persistentAsyncStorage.read(status(TableName.Plan), planId),
                this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInPlan), planId)
            ]);
            if (!plan || planStatus?.state !== StoreEntityState.Present || tasksInPlanStatus?.state !== StoreEntityState.Present) {
                return [];
            }
            const planItem = deserialize(TableName.Plan, plan);
            const taskItems = Object.values(tasks).map((task)=>deserialize(TableName.Tasks, task));
            const deltaLink = tasksInPlanStatus.deltaLink ?? undefined;
            return [
                {
                    type: CacheHydrationRequestType.Single,
                    entityType: EntityType.Plan,
                    data: planItem
                },
                {
                    type: CacheHydrationRequestType.Collection,
                    entityType: EntityType.Task,
                    relationship: QueryRelationship.TasksInPlan,
                    parentId: planId,
                    data: taskItems,
                    deltaLink
                }
            ];
        };
        /**
     * Method to load plan data from the persisted async storage
     * @param planId id of the plan
     */ this.fetchPersistedPlanData = async (planId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const isCFDEnabled = this.configProvider().flights.EnableCustomFields;
            const isCustomViewsEnabled = this.configProvider().flights.EnableCustomViews;
            const isDependenciesEnabled = this.configProvider().flights.EnableDependencies;
            const emptyDependencies = {};
            // Only hydrate from cache when plan tasks are absent from the store
            if (taskStore.getTasksForPlan(planId).status.state !== StoreEntityState.Absent) {
                return;
            }
            this.markHydration(CacheHydrationState.Start, ViewName.BasicPlanPage);
            try {
                const [plan, planStatus, tasks, tasksInPlanStatus, buckets, bucketInPlanStatus, cfd, cfdInPlanStatus, customViews, customViewsInPlanStatus, dependencies, dependenciesInPlanStatus] = await Promise.all([
                    this.persistentAsyncStorage.read(TableName.Plan, planId),
                    this.persistentAsyncStorage.read(status(TableName.Plan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInPlan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Bucket, PlannerPersistedObjectStoreIndexNames.BucketsPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Bucket, QueryRelationship.BucketsInPlan), planId),
                    isCFDEnabled ? this.persistentAsyncStorage.readByIndex(TableName.CustomFieldDefinitions, PlannerPersistedObjectStoreIndexNames.CfdPlanIndex, planId) : Promise.resolve([]),
                    isCFDEnabled ? this.persistentAsyncStorage.read(collection(TableName.CustomFieldDefinitions, QueryRelationship.CustomFieldDefinitionsInPlan), planId) : Promise.resolve(undefined),
                    isCustomViewsEnabled ? this.persistentAsyncStorage.readByIndex(TableName.CustomViews, PlannerPersistedObjectStoreIndexNames.CustomViewPlanIndex, planId) : Promise.resolve([]),
                    isCustomViewsEnabled ? this.persistentAsyncStorage.read(collection(TableName.CustomViews, QueryRelationship.CustomViewsInPlan), planId) : Promise.resolve(undefined),
                    isDependenciesEnabled ? this.persistentAsyncStorage.readByIndex(TableName.Dependency, PlannerPersistedObjectStoreIndexNames.DependencyPlanIndex, planId) : Promise.resolve(emptyDependencies),
                    isDependenciesEnabled ? this.persistentAsyncStorage.read(collection(TableName.Dependency, QueryRelationship.DependenciesInPlan), planId) : Promise.resolve(undefined)
                ]);
                if (planStatus?.state !== StoreEntityState.Present || tasksInPlanStatus?.state !== StoreEntityState.Present || bucketInPlanStatus?.state !== StoreEntityState.Present || isCFDEnabled && cfdInPlanStatus?.state !== StoreEntityState.Present || isCustomViewsEnabled && customViewsInPlanStatus?.state !== StoreEntityState.Present || isDependenciesEnabled && dependenciesInPlanStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103022 /* tag_4eda8 */ , TraceLevel.Info, `BasicPlanPage collection status not present [Planstatus=${planStatus?.state}][TasksInPlanstatus=${tasksInPlanStatus?.state}][BucketInPlanStatus=${bucketInPlanStatus?.state}]${isCFDEnabled ? `[CfdInPlanStatus=${cfdInPlanStatus?.state}]` : ""}${isCustomViewsEnabled ? `[CustomViewsInPlanStatus=${customViewsInPlanStatus?.state}]` : ""}${isDependenciesEnabled ? `[DependenciesInPlanStatus=${dependenciesInPlanStatus?.state}]` : ""}`);
                    return;
                }
                if (!plan) {
                    this.loggers.traceLogger.logTrace(0x1e10218a /* tag_4ecgk */ , TraceLevel.Info, `PlanPage - No entries in cache [Store=PlanStore]`);
                    return;
                }
                try {
                    // deserialize plan
                    const planItem = deserialize(TableName.Plan, plan);
                    // deserialize tasks
                    const taskItems = [];
                    const tasksDeltaLink = tasksInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(tasks).length > 0) {
                        for (const key of Object.keys(tasks)){
                            const item = deserialize(TableName.Tasks, tasks[key]);
                            taskItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102189 /* tag_4ecgj */ , TraceLevel.Info, `No plan tasks in cache [Store=TaskStore]`);
                    }
                    // deserialize buckets
                    const bucketItems = [];
                    const bucketsDeltaLink = bucketInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(buckets).length > 0) {
                        for (const key of Object.keys(buckets)){
                            const item = deserialize(TableName.Bucket, buckets[key]);
                            bucketItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102188 /* tag_4ecgi */ , TraceLevel.Info, `No plan buckets in cache [Store=BucketStore]`);
                    }
                    const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
                    const hydrations = [
                        {
                            type: CacheHydrationRequestType.Single,
                            entityType: EntityType.Plan,
                            data: planItem
                        },
                        {
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.Task,
                            relationship: QueryRelationship.TasksInPlan,
                            parentId: planId,
                            data: taskItems,
                            deltaLink: tasksDeltaLink
                        },
                        {
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.Bucket,
                            relationship: QueryRelationship.BucketsInPlan,
                            parentId: planId,
                            data: bucketItems,
                            deltaLink: bucketsDeltaLink
                        }
                    ];
                    if (isDependenciesEnabled) {
                        const dependencyItems = Object.values(dependencies).map((dependency)=>deserialize(TableName.Dependency, dependency));
                        const dependenciesDeltaLink = dependenciesInPlanStatus?.deltaLink ?? undefined;
                        hydrations.push({
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.Dependency,
                            relationship: QueryRelationship.DependenciesInPlan,
                            parentId: planId,
                            data: dependencyItems,
                            deltaLink: dependenciesDeltaLink
                        });
                    }
                    // deserialize custom fields for plan
                    if (isCFDEnabled) {
                        const cfdItems = [];
                        const cfdDeltaLink = cfdInPlanStatus?.deltaLink ?? undefined;
                        if (Object.keys(cfd).length > 0) {
                            for (const key of Object.keys(cfd)){
                                const item = deserialize(TableName.CustomFieldDefinitions, cfd[key]);
                                cfdItems.push(item);
                            }
                        } else {
                            this.loggers.traceLogger.logTrace(0x1d8997ca /* tag_38z5k */ , TraceLevel.Info, `No cfd for plan in cache [Store=CFDStore]`);
                        }
                        hydrations.push({
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.CustomFieldDefinition,
                            relationship: QueryRelationship.CustomFieldDefinitionsInPlan,
                            parentId: planId,
                            data: cfdItems,
                            deltaLink: cfdDeltaLink
                        });
                    }
                    // deserialize custom views for plan
                    if (isCustomViewsEnabled) {
                        const customViewItems = [];
                        const customViewsDeltaLink = customViewsInPlanStatus?.deltaLink ?? undefined;
                        if (Object.keys(customViews).length > 0) {
                            for (const key of Object.keys(customViews)){
                                const item = deserialize(TableName.CustomViews, customViews[key]);
                                customViewItems.push(item);
                            }
                        } else {
                            this.loggers.traceLogger.logTrace(0x1d75f495 /* tag_335sv */ , TraceLevel.Info, `No custom views for plan in cache [Store=CustomViewStore]`);
                        }
                        hydrations.push({
                            type: CacheHydrationRequestType.Collection,
                            entityType: EntityType.CustomView,
                            relationship: QueryRelationship.CustomViewsInPlan,
                            parentId: planId,
                            data: customViewItems,
                            deltaLink: customViewsDeltaLink
                        });
                    }
                    dataFetchAC.hydrateEntities(hydrations, ()=>this.markHydration(CacheHydrationState.End, ViewName.BasicPlanPage));
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e102187 /* tag_4ecgh */ , TraceLevel.Error, `Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e102186 /* tag_4ecgg */ , TraceLevel.Error, `fetchPersistedPlanData [Error=${error}]`);
            }
        };
        /**
     * Hydrate a single task entity and/or its chat from IndexedDB. Triggered when the active
     * route contains a task id (deep link to a focused task)
     *
     * Reading the task gives the task editor / chat panel its parent task context even when
     * the surrounding plan or list hasn't been hydrated through another code path.
     *
     * Note on deltaLink: we intentionally do NOT pass through the persisted deltaLink. The
     * cached chat is treated as a render-speed optimization only — the slice is left without
     * a delta-sync token so the next `ChatScenarioModule.fetchChatMessages` falls into the
     * "no deltaLink → full fetch" branch and refreshes from server. This avoids keeping a
     * potentially-stale token alive (and the mark-pending-refresh plumbing it would need)
     * while still giving the user instant cached chat during the round trip.
     */ this.fetchPersistedTaskAndChat = async (taskId)=>{
            const [taskStore, chatStore] = this.storeLoader.loadAll([
                Store.Tasks,
                Store.Chat
            ]);
            const taskInStore = taskStore.getTask(taskId);
            const needsTask = taskInStore.status.state === StoreEntityState.Absent;
            const taskInStoreHasChat = taskInStore.data?.hasChat;
            const isChatPersistenceEnabled = this.configProvider().settings.evoPersistenceSettings.chat;
            const needsChat = isChatPersistenceEnabled && chatStore.getChatMessagesForTask(taskId).status.state === StoreEntityState.Absent && taskInStoreHasChat !== false;
            if (!needsTask && !needsChat) {
                return;
            }
            this.markHydration(CacheHydrationState.Start, ViewName.TaskEditorContainer);
            try {
                const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
                const taskRead = needsTask ? this.persistentAsyncStorage.read(TableName.Tasks, taskId) : Promise.resolve(undefined);
                const chatStatusRead = needsChat ? this.persistentAsyncStorage.read(collection(TableName.ChatMessage, QueryRelationship.ChatMessagesForTask), taskId) : Promise.resolve(undefined);
                const [task, chatStatus] = await Promise.all([
                    taskRead,
                    chatStatusRead
                ]);
                const hydrations = [];
                let effectiveTaskHasChat = taskInStoreHasChat;
                if (needsTask && task) {
                    const deserializedTask = deserialize(TableName.Tasks, task);
                    hydrations.push({
                        type: CacheHydrationRequestType.Single,
                        entityType: EntityType.Task,
                        data: deserializedTask
                    });
                    effectiveTaskHasChat = deserializedTask.hasChat;
                }
                // If we were expecting to hydrate the task but no task record is available in cache,
                // don't hydrate chat alone (chat hydration depends on having the parent task context).
                if (needsTask && !task) {
                    return;
                }
                const shouldHydrateChat = effectiveTaskHasChat !== false && needsChat && chatStatus?.state === StoreEntityState.Present;
                if (shouldHydrateChat) {
                    const chatMessages = await this.persistentAsyncStorage.readByIndex(TableName.ChatMessage, PlannerPersistedObjectStoreIndexNames.ChatMessagesParentEntityIndex, taskId);
                    const messages = Object.values(chatMessages).map((msg)=>deserialize(TableName.ChatMessage, msg));
                    // deltaLink is left off — see method JSDoc for why we drop it on hydration.
                    hydrations.push({
                        type: CacheHydrationRequestType.Collection,
                        entityType: EntityType.ChatMessage,
                        relationship: QueryRelationship.ChatMessagesForTask,
                        parentId: taskId,
                        data: messages
                    });
                }
                if (hydrations.length > 0) {
                    dataFetchAC.hydrateEntities(hydrations, ()=>this.markHydration(CacheHydrationState.End, ViewName.TaskEditorContainer));
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d85d6cd /* tag_3731n */ , TraceLevel.Error, `fetchPersistedTaskAndChat [TaskId=${taskId}][Error=${error}]`);
            }
        };
        /**
     * Method to fetch view config from cache and hydrate the view config store.
     * Reads from (in order): new localStorage key -> IndexedDB -> old localStorage keys (Uno format)
     * @param viewId The id of the view
     */ this.fetchAndHydrateViewConfig = async (viewId)=>{
            this.markHydration(CacheHydrationState.Start, viewId.name);
            try {
                const viewConfig = this.readViewConfigFromLocalStorage(viewId) ?? await this.readViewConfigFromIndexedDB(viewId) ?? this.readViewConfigFromUno(viewId);
                if (viewConfig) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateViewConfig(viewId, deserialize(TableName.ViewConfig, viewConfig), ()=>this.markHydration(CacheHydrationState.End, viewId.name));
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e0d614d /* tag_4dwfn */ , TraceLevel.Error, `fetchAndHydrateViewConfig [Error=${error}]`);
            }
        };
        /**
     * Method to fetch the global task editor render mode from localStorage and hydrate the store.
     */ this.fetchAndHydrateTaskEditorRenderMode = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(GlobalViewSettingKeys.TaskEditorRenderMode);
                if (data) {
                    const renderMode = JSON.parse(data);
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorRenderMode(renderMode);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e053181 /* tag_4btgb */ , TraceLevel.Error, `fetchAndHydrateTaskEditorRenderMode [Error=${error}]`);
            }
        };
        /**
     * Method to fetch the global task editor active side panel state from localStorage and hydrate the store.
     */ this.fetchAndHydrateTaskEditorActiveSidePanel = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(ViewStoreTaskEditorActiveSidePanelKey);
                if (data) {
                    const activeSidePanel = JSON.parse(data);
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorActiveSidePanel(activeSidePanel);
                    return;
                }
                // Migration: fall back to the legacy chat-pane-open boolean when the new key has not been
                // written yet, so a user who previously kept the chat pane open keeps it open. A stored
                // `true` maps to the Chat panel; `false`/absent leaves the store default untouched.
                const legacyData = this.persistedStorage.getItem(LegacyViewStoreTaskChatPaneOpenKey);
                if (legacyData && JSON.parse(legacyData)) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorActiveSidePanel(TaskEditorSidePanelType.Chat);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d80a392 /* tag_36kos */ , TraceLevel.Error, `fetchAndHydrateTaskEditorActiveSidePanel [Error=${error}]`);
            }
        };
        /** Hydrates the global Task Editor side panel width from localStorage. */ this.fetchAndHydrateTaskEditorSidePanelWidth = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(ViewStoreTaskEditorSidePanelWidthKey);
                if (!data) {
                    return;
                }
                const width = JSON.parse(data);
                if (typeof width === "number" && Number.isFinite(width)) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorSidePanelWidth(width);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d7cf693 /* tag_35p0t */ , TraceLevel.Error, `fetchAndHydrateTaskEditorSidePanelWidth [Error=${error}]`);
            }
        };
        this.readViewConfigFromIndexedDB = async (viewId)=>{
            let viewConfig = await this.persistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
            if (viewConfig) {
                return viewConfig;
            }
            // Try to Read from old IndexedDB (persisted async storage)
            // This is only needed in some environments where we need to rev the cache and still retain data stored in view table for eg: filters
            // These environments include dev, demo, dogfood [dev, staging, all] & MSIT
            // We have disabled tasks persistence currently and need to rev cache before enabling it back again
            // ToDo: This code to be cleaned up after some stabilization time to help migrate the view table correctly.
            if (this.configProvider().settings.evoPersistentAsyncStorageVersion > 1) {
                const rolloutStage = this.configProvider().settings.rolloutStage;
                if (rolloutStage === RolloutStage.ProdMSIT || rolloutStage === RolloutStage.DogfoodDev || rolloutStage === RolloutStage.DogfoodStaging || rolloutStage === RolloutStage.DogfoodAll || rolloutStage === RolloutStage.Demo || rolloutStage === RolloutStage.Dev) {
                    const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
                    const oldPersistentAsyncStorage = new IndexedDBStorage(userId, "PlannerV2_1", PlannerV2AsyncStorageSchemaVersionV1, PersistedObjectStoreSchema, this.loggers.traceLogger, this.loggers.performanceMarker);
                    viewConfig = await oldPersistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
                    if (viewConfig) {
                        this.loggers.traceLogger.logTrace(0x1e05c1df /* tag_4b2h5 */ , TraceLevel.Info, `readViewConfigFromIndexedDB: read view config from old indexedDB`);
                        return viewConfig;
                    }
                }
            }
            return null;
        };
    }
}
